import { useEffect, useState, useCallback, createContext, useContext } from "react";
import { NavLink, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { LayoutGrid, FolderOpen, Files, BookMarked, Upload, CreditCard, UserRound, Loader2 } from "lucide-react";
import { webApi } from "@/lib/webApi";
import { Overview, Matters, MatterDetail } from "@/pages/web/WebMatters";
import { FilesView } from "@/pages/web/WebFiles";
import { UploadView } from "@/pages/web/WebUpload";
import { Billing, Account } from "@/pages/web/WebAccount";

const MeCtx = createContext(null);
export const useMe = () => useContext(MeCtx);

const NAV = [
  ["/app", "Overview", LayoutGrid, true], ["/app/matters", "Matters", FolderOpen], ["/app/files", "Files", Files],
  ["/app/library", "Library", BookMarked], ["/app/upload", "Upload", Upload], ["/app/billing", "Plan", CreditCard],
  ["/app/account", "Account", UserRound],
];

function Nav() {
  const cls = ({ isActive }) =>
    `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors duration-200 ${isActive ? "bg-amber-300/10 text-amber-200" : "text-slate-400 hover:bg-white/5 hover:text-slate-100"}`;
  const mob = ({ isActive }) => `flex flex-1 flex-col items-center gap-1 py-2 text-[10px] ${isActive ? "text-amber-200" : "text-slate-500"}`;
  return (
    <>
      <aside className="fixed inset-y-0 left-0 hidden w-56 border-r border-white/5 bg-[#090C12] px-3 py-6 md:block" data-testid="web-sidebar">
        <div className="mb-8 px-3 font-display text-2xl font-bold text-slate-100">Dicta</div>
        <nav className="space-y-1">
          {NAV.map(([to, label, Icon, end]) => (
            <NavLink key={to} to={to} end={end} className={cls} data-testid={`web-nav-${label.toLowerCase()}`}><Icon className="h-4 w-4" /> {label}</NavLink>
          ))}
        </nav>
      </aside>
      <nav className="fixed inset-x-0 bottom-0 z-30 flex border-t border-white/10 bg-[#090C12]/95 backdrop-blur md:hidden" data-testid="web-bottom-bar">
        {NAV.map(([to, label, Icon, end]) => (
          <NavLink key={to} to={to} end={end} className={mob} data-testid={`web-mnav-${label.toLowerCase()}`}><Icon className="h-4 w-4" />{label}</NavLink>
        ))}
      </nav>
    </>
  );
}

export default function WebApp() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const load = useCallback(() => webApi.get("/me").then((r) => setMe(r.data)).catch(() => nav("/app/login", { replace: true })), [nav]);

  useEffect(() => {
    load();
  }, [load]);

  if (!me) return <div className="flex min-h-screen items-center justify-center bg-[#07090D]"><Loader2 className="h-5 w-5 animate-spin text-slate-500" /></div>;
  return (
    <MeCtx.Provider value={{ me, reload: load }}>
      <div className="min-h-screen bg-[#07090D] text-slate-200" data-testid="web-app">
        <Nav />
        <main className="px-4 pb-24 pt-6 sm:px-8 md:ml-56 md:pb-10">
          <div className="mx-auto max-w-6xl">
            <Routes>
              <Route index element={<Overview />} />
              <Route path="matters" element={<Matters />} />
              <Route path="matters/:id" element={<MatterDetail />} />
              <Route path="files" element={<FilesView />} />
              <Route path="library" element={<FilesView library />} />
              <Route path="upload" element={<UploadView />} />
              <Route path="billing" element={<Billing />} />
              <Route path="account" element={<Account />} />
              <Route path="*" element={<Navigate to="/app" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </MeCtx.Provider>
  );
}

export function PageHead({ title, sub, children }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-100 sm:text-4xl" data-testid="web-page-title">{title}</h1>
        {sub && <p className="mt-1 text-sm text-slate-400">{sub}</p>}
      </div>
      <div className="flex gap-2">{children}</div>
    </div>
  );
}

export function Empty({ children }) {
  return <div className="rounded-lg border border-dashed border-white/10 p-8 text-center text-sm text-slate-500" data-testid="web-empty">{children}</div>;
}

export const btn = "inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors duration-200";
export const primary = `${btn} bg-amber-300 font-semibold text-[#07090D] hover:bg-amber-200`;
export const ghost = `${btn} border border-white/10 text-slate-300 hover:bg-white/5`;
